import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditSectorMutation } from "@modules/bookSectors/services/mutations";
import { SectorFormValues, sectorSchema } from "@modules/bookSectors/validations";
import { listSectorsQueryKey } from "@modules/bookSectors/services/queries";

export function useEditSectorModal(modalRef: RefObject<ModalRef>, onComplete: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<SectorFormValues>({
    resolver: zodResolver(sectorSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditSectorMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listSectorsQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("book_sector_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: SectorFormValues) => {
    const itemId = modalRef.current?.getId();
    await mutateAsync({ id: itemId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
